import { useMemo } from 'react';

import { useSingleListSearchPage } from 'src/hooks/useSearchPage';
import { flattenFilterOptions } from 'src/hooks/useSearchPage/filterOptions';
import { CriteriaFacetStates } from 'src/hooks/useSearchPage/sharedTypes';
import { SearchFacetsFetcher } from 'src/hooks/useSearchPage/useSearchFacetsState';
import { useSite } from 'src/hooks/useSite';

import { SearchFilterConfig } from 'src/types/components';

export const peopleSearchFilterNames = [
  'lastnameletter',
  'relatedservices',
  'relatedoffices',
  'baradmissions',
  'lawyerlevel',
] as const;

export type PeopleSearchFilterName = (typeof peopleSearchFilterNames)[number];

export type PeopleSearchResultItem = {
  id: number;
  email: string;
  phone: string;
  image?: {
    src: string;
    alt: string;
  };
  firstName: string;
  middleName: string;
  lastName: string;
  title: string;
  office: {
    href: string;
    label: string;
  };
  url: string;
};

type UsePeopleSearchConfig = {
  resultsPerPage: number;
  filters: SearchFilterConfig[];
  initialFacetState: CriteriaFacetStates<PeopleSearchFilterName> | undefined;
};

export const usePeopleSearch = ({
  resultsPerPage,
  filters,
  initialFacetState,
}: UsePeopleSearchConfig) => {
  const { site } = useSite();
  const filterDefinitions = useMemo(
    () =>
      filters.reduce<
        Record<string, { label: string; options: Array<{ label: string; value: string }> }>
      >((definitions, filter) => {
        definitions[filter.name] = {
          label: filter.label,
          options: flattenFilterOptions(filter.options),
        };

        return definitions;
      }, {}),
    [filters]
  );

  const facetsFetcher = useMemo<SearchFacetsFetcher<PeopleSearchFilterName>>(
    () =>
      async (criteria, { signal }) => {
        const params = new URLSearchParams();

        params.set('facetsOnly', 'true');
        params.set('site', site);

        if (criteria.query) {
          params.set('query', criteria.query);
        }

        Object.entries<string[]>(criteria.filters).forEach(([filterName, values]) => {
          values.forEach((value) => {
            params.append(filterName, value);
          });
        });

        const response = await fetch(`/api/people-search/search?${params.toString()}`, {
          signal,
        });

        if (!response.ok) {
          throw new Error('Unable to load people search facets.');
        }

        return response.json();
      },
    [site]
  );

  const search = useSingleListSearchPage<
    PeopleSearchResultItem,
    PeopleSearchFilterName,
    string,
    string | null
  >({
    fetcher: async ({ cursor, filters, query, signal, sort, take }) => {
      const params = new URLSearchParams();

      if (query) {
        params.set('query', query);
      }

      if (sort) {
        params.set('sort', sort);
      }

      params.set('take', String(take));

      params.set('site', site);

      if (cursor) {
        params.set('cursor', cursor);
      }

      Object.entries(filters).forEach(([filterName, values]) => {
        values.forEach((value) => {
          params.append(filterName, value);
        });
      });

      const response = await fetch(`/api/people-search/search?${params.toString()}`, { signal });

      if (!response.ok) {
        throw new Error('Unable to load people search results.');
      }

      return response.json();
    },
    filters: filterDefinitions,
    pageSize: resultsPerPage,
    initialFacetState,
    facetsFetcher,
  });
  return search;
};
